// Security review: distinguish chain confirmation from delayed activity registration.
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { Api } from '../lib/api.js';
import { roleSummary } from '../lib/agent-text.js';
import { useApi, useWallet } from '../lib/wallet.jsx';
import { Badge, Button, Card, Empty, ErrorState, Field, Hash, Modal, Skeleton } from '../components/ui.jsx';

/**
 * Agent roster.
 *
 * §12.13: each agent is singular and valuable, so this is a roster of
 * relationships rather than a grid of collectible cards. There is no "create
 * more" incentive anywhere on this screen, and the cap is stated plainly rather
 * than being a surprise at the fifth attempt.
 */
export function Roster({ pushToast }) {
  const { sessionReady } = useWallet();
  const { data, loading, error, reload } = useApi(() => Api.dashboard(), [sessionReady], { skip: !sessionReady });
  const [creating, setCreating] = useState(false);

  if (error) return <ErrorState error={error} onRetry={reload} context="The roster could not be loaded" />;
  if (!sessionReady || loading || !data) return <Skeleton rows={5} />;

  const agents = data.agents;

  return (
    <div className="stack">
      <header className="page-header">
        <div>
          <h1 className="page-title">Agents</h1>
          <p className="page-subtitle">
            {agents.length} {agents.length === 1 ? 'agent' : 'agents'}. Each passport is soulbound to your account and cannot be transferred or sold.
          </p>
        </div>
        <Button variant="primary" onClick={() => setCreating(true)}>
          New agent
        </Button>
      </header>

      {agents.length === 0 ? (
        <Empty
          title="No agents yet"
          description="An agent is a single specialist bound to your account. Create a draft first, then mint its passport on chain."
          action={<Button variant="primary" onClick={() => setCreating(true)}>Create an agent</Button>}
        />
      ) : (
        <div className="roster">
          {agents.map((a) => (
            <AgentCard key={a.id} agent={a} engagement={data.engagement?.[a.id]} wallet={data.walletEngagement} onChange={reload} pushToast={pushToast} />
          ))}
        </div>
      )}

      <CreateAgentModal
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={() => {
          setCreating(false);
          reload();
        }}
        pushToast={pushToast}
      />
    </div>
  );
}

function AgentCard({ agent, engagement, wallet, onChange, pushToast }) {
  const { sendIntent, isAuthenticated } = useWallet();
  const [busy, setBusy] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const act = async (kind, fn) => {
    setBusy(kind);
    try {
      const result = await fn();
      pushToast({ tone: 'success', message: result });
      onChange();
    } catch (err) {
      pushToast({ tone: 'danger', message: err.message });
    } finally {
      setBusy(null);
      setConfirm(null);
    }
  };

  const mint = () =>
    act('mint', async () => {
      const { intent } = await Api.mintIntent(agent.id);
      const r = await sendIntent({ intent, txType: 'mint', agentId: agent.id });
      if (r.status !== 'success') throw new Error('The mint transaction reverted');
      return `Passport minted in block ${r.blockNumber}.${r.registrationError ? ` ${r.registrationError}` : ''}`;
    });

  const checkIn = () =>
    act('check-in', async () => {
      const { intent } = await Api.checkInIntent(agent.id);
      const r = await sendIntent({ intent, txType: 'check_in', agentId: agent.id });
      if (r.status !== 'success') throw new Error('The check-in transaction reverted');
      return `Checked in. One point awarded.${r.registrationError ? ` ${r.registrationError}` : ''}`;
    });

  const archive = () =>
    act('archive', async () => {
      const r = await Api.deleteAgent(agent.id);
      return r.archived ? 'Agent archived. Its chain history is retained.' : 'Draft deleted.';
    });

  const minted = Boolean(agent.passportTokenId);

  return (
    <Card className="agent-card">
      <div className="agent-card__head">
        <div>
          <Link className="agent-card__name" to={`/agents/${agent.id}`}>{agent.name}</Link>
          <p className="agent-card__role">{roleSummary(agent.role)}</p>
        </div>
        <div className="agent-card__badges">
          {minted ? <Badge tone="brand">Passport #{agent.passportTokenId}</Badge> : <Badge tone="pending">Draft</Badge>}
          <Badge tone="neutral">Soulbound</Badge>
        </div>
      </div>

      <dl className="agent-card__stats">
        <Stat label="Points" value={wallet ? wallet.points : '—'} />
        <Stat label="Check-ins" value={wallet ? wallet.checkInCount : '—'} />
        <Stat label="Streak" value={wallet ? `${wallet.currentStreak}d` : '—'} />
        <Stat label="Workflows" value={engagement ? engagement.workflowCount : '—'} />
      </dl>


      <div className="agent-card__actions">
        <Link className="button button--secondary" to={`/agents/${agent.id}`}>Open</Link>
        {!minted && (
          <Button variant="primary" loading={busy === 'mint'} onClick={mint} disabled={!isAuthenticated}>
            Mint passport
          </Button>
        )}
        {minted && (
          <>
            <Button
              variant="secondary"
              loading={busy === 'check-in'}
              onClick={checkIn}
              disabled={engagement?.checkedInToday}
              title={engagement?.checkedInToday ? 'Already checked in today' : undefined}
            >
              {engagement?.checkedInToday ? 'Checked in' : 'Check in'}
            </Button>
          </>
        )}
        <Button variant="ghost" onClick={() => setConfirm('archive')}>
          {minted ? 'Archive' : 'Delete'}
        </Button>
      </div>

      {!isAuthenticated && (
        <p className="agent-card__gate text-muted">
          Connect a wallet to {minted ? 'check in' : 'mint this passport'} on chain. Everything else here works without one.
        </p>
      )}


      <Modal
        open={confirm === 'archive'}
        title={minted ? 'Archive this agent' : 'Delete this draft'}
        onClose={() => setConfirm(null)}
        footer={
          <>
            <Button variant="ghost" onClick={() => setConfirm(null)}>Cancel</Button>
            <Button variant="danger" loading={busy === 'archive'} onClick={archive}>
              {minted ? 'Archive' : 'Delete'}
            </Button>
          </>
        }
      >
        {minted ? (
          <p>
            Archiving frees a roster slot. The passport and every proof it produced remain on chain and in your
            history; nothing is erased.
          </p>
        ) : (
          <p>This draft has no passport and has never been on chain, so it can be deleted outright.</p>
        )}
      </Modal>
    </Card>
  );
}

function Stat({ label, value }) {
  return (
    <div className="stat">
      <dt className="stat__label">{label}</dt>
      <dd className="stat__value mono">{value}</dd>
    </div>
  );
}

function CreateAgentModal({ open, onClose, onCreated, pushToast }) {
  const [name, setName] = useState('');
  const [role, setRole] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await Api.createAgent({ name, role });
      setName('');
      setRole('');
      pushToast({ tone: 'success', message: 'Draft created. Mint its passport when you are ready.' });
      onCreated();
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      open={open}
      title="New agent"
      onClose={onClose}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button variant="primary" loading={busy} onClick={submit} disabled={!name || !role}>Create draft</Button>
        </>
      }
    >
      <Field label="Name" htmlFor="agent-name" hint="How you will refer to this specialist.">
        <input id="agent-name" className="input" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} />
      </Field>
      <Field label="Role" htmlFor="agent-role" hint="What this agent is for. Shapes its retrieval and tools.">
        <input id="agent-role" className="input" value={role} onChange={(e) => setRole(e.target.value)} maxLength={120} />
      </Field>
      {/* Creating a draft is deliberately separate from minting: nothing here
          claims a passport exists until a receipt says so. */}
      <p className="text-muted">
        This creates a draft only. No transaction is sent and no passport exists until one is minted and the receipt
        confirms.
      </p>
      {error && <p className="field__error" role="alert">{error}</p>}
    </Modal>
  );
}
